import { motion } from 'framer-motion';
import { CheckCircle2, Upload, ArrowRight, RotateCcw } from 'lucide-react';
import { AnimatedButton } from '../shared/AnimatedButton';
import type { ClaimTheme } from '../constants';

interface BookingFoundSectionProps {
  flightCount: number;
  passengerCount: number;
  onContinue: () => void;
  onReupload: () => void;
  onStartFresh?: () => void;
  theme?: ClaimTheme;
}

export function BookingFoundSection({
  flightCount,
  passengerCount,
  onContinue,
  onReupload,
  onStartFresh,
  theme = 'invol',
}: BookingFoundSectionProps) {
  return (
    <motion.div
      key="booking-found"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex flex-col"
    >
      {/* Success icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 20,
          delay: 0.1,
        }}
        className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/25"
      >
        <CheckCircle2 className="h-8 w-8 text-white" />
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-3xl font-bold text-slate-900 mb-2"
      >
        Wir haben Ihre Flüge gefunden!
      </motion.h2>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-lg text-slate-500 mb-2"
      >
        {flightCount} {flightCount !== 1 ? 'Flüge' : 'Flug'} und{' '}
        {passengerCount} Passagier{passengerCount !== 1 ? 'e' : ''} extrahiert.
      </motion.p>

      {/* Help text */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="text-sm text-slate-400 mb-8"
      >
        Überprüfen Sie die Details auf der linken Seite und klicken Sie zum Bearbeiten, falls erforderlich.
      </motion.p>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="flex flex-col gap-3"
      >
        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={onContinue}
          icon={<ArrowRight className="w-5 h-5" />}
          theme={theme}
        >
          Sieht gut aus, weiter
        </AnimatedButton>

        <button
          onClick={onReupload}
          className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors py-2"
        >
          <Upload className="w-4 h-4" />
          Eine andere Datei hochladen
        </button>

        {onStartFresh && (
          <button
            onClick={onStartFresh}
            className="flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-colors py-2"
          >
            <RotateCcw className="w-4 h-4" />
            Verwerfen und neu starten
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}
